import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  CalculatorIcon,
  UserGroupIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const InvoiceCreate = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    partnerId: '',
    companyId: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default 30 days
    currency: 'BGN',
    selectedBankAccount: '',
    projectId: '',
    items: [
      {
        description: '',
        quantity: 1,
        unitPrice: 0,
        total: 0
      }
    ],
    notes: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (message.type === 'success' && message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      // Fetch partners - only active partners for invoice creation
      const partnersResponse = await fetch('/api/finance/partners?limit=100&activeOnly=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch companies
      const companiesResponse = await fetch('/api/finance/companies', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch projects
      const projectsResponse = await fetch('/api/projects/list', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (partnersResponse.ok && companiesResponse.ok && projectsResponse.ok) {
        const partnersData = await partnersResponse.json();
        const companiesData = await companiesResponse.json();
        const projectsData = await projectsResponse.json();
        
        setPartners(partnersData.partners || []);
        setCompanies(companiesData);
        setProjects(projectsData.projects || []);
      } else {
        throw new Error('Failed to fetch initial data');
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
              setMessage({ type: 'error', text: t('invoiceCreate.failedToLoadData') });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle partner selection
    if (name === 'partnerId') {
      if (value) {
        const selectedPartner = partners.find(partner => partner._id === value);
        if (selectedPartner && selectedPartner.paymentTerms) {
          // Calculate due date based on partner's payment terms
          const paymentDays = parseInt(selectedPartner.paymentTerms) || 30;
          const dueDate = new Date(Date.now() + paymentDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          
          setFormData(prev => ({
            ...prev,
            partnerId: value,
            dueDate: dueDate
          }));
        } else {
          // Partner selected but no payment terms, use default 30 days
          const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          setFormData(prev => ({
            ...prev,
            partnerId: value,
            dueDate: dueDate
          }));
        }
      } else {
        // No partner selected, use default 30 days
        const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        setFormData(prev => ({
          ...prev,
          partnerId: value,
          dueDate: dueDate
        }));
      }
    }

    // Handle company selection
    if (name === 'companyId') {
      const selectedCompanyData = companies.find(company => company._id === value);
      setSelectedCompany(selectedCompanyData);
      
      if (selectedCompanyData) {
        // Find default bank account for the selected company
        const defaultBankIndex = selectedCompanyData.bankAccounts?.findIndex(bank => bank.isDefault) || 0;
        
        // Set the default bank account when company changes
        setFormData(prev => ({
          ...prev,
          companyId: value,
          selectedBankAccount: selectedCompanyData.bankAccounts?.length > 0 ? defaultBankIndex.toString() : ''
        }));
      } else {
        // No company selected, clear bank account
        setFormData(prev => ({
          ...prev,
          companyId: value,
          selectedBankAccount: ''
        }));
      }
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    
    // Handle description field with character limit and line breaks
    if (field === 'description') {
      // Limit to 55 characters per line and add line breaks
      const lines = value.split('\n');
      const processedLines = lines.map(line => {
        if (line.length > 55) {
          // Break at 55 characters, try to break at word boundary
          const words = line.split(' ');
          let currentLine = '';
          let result = '';
          
          for (let word of words) {
            if ((currentLine + word).length <= 55) {
              currentLine += (currentLine ? ' ' : '') + word;
            } else {
              if (currentLine) {
                result += (result ? '\n' : '') + currentLine;
                currentLine = word;
              } else {
                // Word is longer than 55 chars, force break
                result += (result ? '\n' : '') + word.substring(0, 55);
                currentLine = word.substring(55);
              }
            }
          }
          
          if (currentLine) {
            result += (result ? '\n' : '') + currentLine;
          }
          
          return result;
        }
        return line;
      });
      
      value = processedLines.join('\n');
    }
    
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Calculate total for this item (subtotal only, no tax)
    const quantity = parseFloat(updatedItems[index].quantity) || 0;
    const unitPrice = parseFloat(updatedItems[index].unitPrice) || 0;
    
    const total = quantity * unitPrice; // Item total is just quantity × unit price

    updatedItems[index].total = total;

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: '',
          quantity: 1,
          unitPrice: 0,
          total: 0
        }
      ]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      return sum + (quantity * unitPrice);
    }, 0);

    // Fixed tax rate of 20%
    const taxRate = 20;
    const taxAmount = subtotal * (taxRate / 100);

    const totalAmount = subtotal + taxAmount;

    return { subtotal, taxAmount, totalAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.partnerId) {
      setMessage({ type: 'error', text: t('invoiceCreate.pleaseSelectPartner') });
      return;
    }

    if (formData.items.some(item => !item.description.trim())) {
      setMessage({ type: 'error', text: t('invoiceCreate.pleaseFillItemDescriptions') });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('jwtToken');
      const { subtotal, taxAmount, totalAmount } = calculateTotals();

      // Get the selected bank account details
      const selectedBank = selectedCompany?.bankAccounts?.[formData.selectedBankAccount];
      
      // Add taxRate to each item for backend compatibility
      const itemsWithTaxRate = formData.items.map(item => ({
        ...item,
        taxRate: 20 // Fixed 20% tax rate
      }));

      const invoiceData = {
        ...formData,
        items: itemsWithTaxRate,
        subtotal,
        taxAmount,
        totalAmount,
        companyId: selectedCompany?._id,
        selectedBankAccount: selectedBank ? {
          bankName: selectedBank.bankName,
          swiftCode: selectedBank.swiftCode,
          iban: selectedBank.iban
        } : null
      };

      console.log('Sending invoice data:', invoiceData);

      const response = await fetch('/api/finance/invoices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(invoiceData)
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to create invoice');
      }

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: t('invoiceCreate.invoiceCreatedSuccessfully') });
        
        // Redirect to invoice details after a short delay
        setTimeout(() => {
          window.location.href = `/admin/dashboard/invoices/${data._id}`;
        }, 1500);
      } else {
        throw new Error('Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setMessage({ type: 'error', text: t('invoiceCreate.failedToCreateInvoice') });
    } finally {
      setSaving(false);
    }
  };

  // Euro conversion rate (fixed rate for Bulgaria)
  const EUR_RATE = 1.95583;

  const formatCurrency = (amount, currency = 'BGN') => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('bg-BG');
  };

  const convertToEuro = (bgnAmount) => {
    return bgnAmount / EUR_RATE;
  };

  const formatEuro = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const { subtotal, taxAmount, totalAmount } = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('invoiceCreate.loadingInvoiceForm')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => window.history.back()}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('invoiceCreate.createNewInvoice')}</h1>
              </div>
            </div>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Invoice Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('invoiceCreate.invoiceDetails')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceCreate.company')} *
                </label>
                <select
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('invoiceCreate.selectCompany')}</option>
                  {companies.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceCreate.bankAccountForPayment')}
                </label>
                <select
                  name="selectedBankAccount"
                  value={formData.selectedBankAccount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('invoiceCreate.selectBankAccount')}</option>
                  {selectedCompany?.bankAccounts?.map((bank, index) => (
                    <option key={index} value={index}>
                      {bank.bankName} - {bank.iban} {bank.isDefault ? t('invoiceCreate.default') : ''}
                    </option>
                  ))}
                </select>
              </div>



              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceCreate.partnerClient')} *
                </label>
                <select
                  name="partnerId"
                  value={formData.partnerId}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('invoiceCreate.selectPartner')}</option>
                  {partners.map((partner) => (
                    <option key={partner._id} value={partner._id}>
                      {partner.name} - {partner.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceCreate.project')} ({t('invoiceCreate.optional')})
                </label>
                <select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('invoiceCreate.selectProjectOptional')}</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name} - {project.status}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceCreate.currency')}
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="BGN">{t('invoiceCreate.bgnBulgarianLev')}</option>
                  <option value="EUR">{t('invoiceCreate.eurEuro')}</option>
                  <option value="USD">{t('invoiceCreate.usdUsDollar')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceCreate.issueDate')} *
                </label>
                <input
                  type="text"
                  name="issueDate"
                  value={formatDate(formData.issueDate)}
                  onChange={(e) => {
                    // Convert Bulgarian date format back to ISO format for storage
                    const dateParts = e.target.value.split('.');
                    if (dateParts.length === 3) {
                      const day = dateParts[0];
                      const month = dateParts[1];
                      const year = dateParts[2];
                      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                      setFormData(prev => ({ ...prev, issueDate: isoDate }));
                    }
                  }}
                  placeholder={t('invoiceCreate.dateFormat')}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceCreate.dueDate')} *
                </label>
                <input
                  type="text"
                  name="dueDate"
                  value={formatDate(formData.dueDate)}
                  onChange={(e) => {
                    // Convert Bulgarian date format back to ISO format for storage
                    const dateParts = e.target.value.split('.');
                    if (dateParts.length === 3) {
                      const day = dateParts[0];
                      const month = dateParts[1];
                      const year = dateParts[2];
                      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                      setFormData(prev => ({ ...prev, dueDate: isoDate }));
                    }
                  }}
                  placeholder={t('invoiceCreate.dateFormat')}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">{t('invoiceCreate.items')}</h2>
              <button
                type="button"
                onClick={addItem}
                className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <PlusIcon className="h-4 w-4" />
                <span>{t('invoiceCreate.addItem')}</span>
              </button>
            </div>

            <div className="space-y-4">
              {formData.items.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('invoiceCreate.description')} *
                      </label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        required
                        rows={Math.max(1, Math.ceil(item.description.length / 55))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder={t('invoiceCreate.itemDescription')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('invoiceCreate.quantity')}
                      </label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('invoiceCreate.unitPrice')}
                      </label>
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>



                    <div className="flex items-end space-x-2">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('invoiceCreate.total')}
                        </label>
                        <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-medium text-gray-900">
                          {formatCurrency(item.total, formData.currency)}
                        </div>
                        <div className="px-3 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 mt-1">
                          {formatEuro(convertToEuro(item.total))}
                        </div>
                      </div>
                      
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-900 p-2"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('invoiceCreate.invoiceSummary')}</h2>
            
            <div className="space-y-4">
              <div className="flex justify-between text-lg">
                <span className="text-gray-600">{t('invoiceCreate.subtotal')}:</span>
                <span className="font-medium text-left">{formatCurrency(subtotal, formData.currency)}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span className="text-gray-600">{t('invoiceCreate.taxAmount')}:</span>
                <span className="font-medium text-left">{formatCurrency(taxAmount, formData.currency)}</span>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between text-xl font-bold">
                  <span className="text-gray-900">{t('invoiceCreate.totalAmount')}:</span>
                  <span className="text-blue-600 text-left">{formatCurrency(totalAmount, formData.currency)}</span>
                </div>
              </div>
              
              {/* Euro Conversion */}
              <div className="border-t border-gray-200 pt-4 bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('invoiceCreate.euroConversion')}</h3>
                <div className="space-y-4">
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">{t('invoiceCreate.subtotal')}:</span>
                    <span className="font-medium text-left">{formatEuro(convertToEuro(subtotal))}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">{t('invoiceCreate.taxAmount')}:</span>
                    <span className="font-medium text-left">{formatEuro(convertToEuro(taxAmount))}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between text-xl font-bold">
                      <span className="text-gray-900">{t('invoiceCreate.totalAmount')}:</span>
                      <span className="text-green-600 text-left">{formatEuro(convertToEuro(totalAmount))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('invoiceCreate.additionalNotes')}</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('invoiceCreate.notes')} ({t('invoiceCreate.optional')})
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('invoiceCreate.addNotesPlaceholder')}
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            >
              {t('invoiceCreate.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>{t('invoiceCreate.creatingInvoice')}</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5" />
                  <span>{t('invoiceCreate.createInvoice')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceCreate; 